/**
 * SMSComposeView - Message composition interface
 */
import { Send, User, Users, Phone, AlertTriangle, RefreshCw, Plus, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PERSONALIZATION_TOKENS, calculateMessageStats } from './smsUtils';

export default function SMSComposeView({
  message,
  setMessage,
  recipientType,
  setRecipientType,
  phoneNumber,
  setPhoneNumber,
  campaignId,
  setCampaignId,
  templates,
  loading,
  sending,
  onPreviewRecipients,
  onSendTestSMS,
  onOpenTemplateDialog,
  onApplyTemplate
}) {
  const { charCount, segmentCount, remainingChars } = calculateMessageStats(message);

  const insertToken = (token) => {
    setMessage(prev => prev + token);
  };

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {/* Main Compose Area */}
      <div className="md:col-span-2 space-y-4">
        <div className="bg-card rounded-2xl p-6 space-y-4">
          <h3 className="font-medium text-foreground flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />
            Compose Message
          </h3>
          
          {/* Recipient Type */}
          <div className="space-y-2">
            <Label className="text-foreground">Send To</Label>
            <Select value={recipientType} onValueChange={setRecipientType}>
              <SelectTrigger data-testid="sms-recipient-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Individual Number
                  </div>
                </SelectItem>
                <SelectItem value="campaign">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Campaign Signups
                  </div>
                </SelectItem>
                <SelectItem value="loyalty">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Loyalty Members
                  </div>
                </SelectItem>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    All Subscribers
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Individual Phone Number */}
          {recipientType === 'individual' && (
            <div className="space-y-2">
              <Label className="text-foreground">Phone Number</Label>
              <Input
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+6791234567"
                data-testid="sms-phone-input"
              />
              <p className="text-xs text-muted-foreground">Include country code (e.g., +679 for Fiji)</p>
            </div>
          )}

          {/* Campaign Selection */}
          {recipientType === 'campaign' && (
            <div className="space-y-2">
              <Label className="text-foreground">Campaign</Label>
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campaigns</SelectItem>
                  <SelectItem value="1">Founding Members</SelectItem>
                  <SelectItem value="2">Island Energy</SelectItem>
                  <SelectItem value="3">Grand Opening</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Message Input */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-foreground">Message</Label>
              <div className="flex items-center gap-2 text-xs">
                <span className={charCount > 160 ? 'text-amber-500' : 'text-muted-foreground'}>
                  {charCount} chars
                </span>
                <span className="text-muted-foreground">|</span>
                <span className={segmentCount > 1 ? 'text-amber-500 font-medium' : 'text-muted-foreground'}>
                  {segmentCount} segment{segmentCount !== 1 ? 's' : ''}
                </span>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">{remainingChars} left</span>
              </div>
            </div>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={5}
              className="resize-none"
              data-testid="sms-message-input"
            />
            {segmentCount > 1 && (
              <p className="text-xs text-amber-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Messages over 160 characters are sent as multiple segments (additional cost)
              </p>
            )}
          </div>

          {/* Personalization Tokens */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Insert Personalization</Label>
            <div className="flex flex-wrap gap-2">
              {PERSONALIZATION_TOKENS.map(({ token, description }) => (
                <button
                  key={token}
                  onClick={() => insertToken(token)}
                  className="px-3 py-1 text-xs bg-muted rounded-full hover:bg-primary hover:text-white transition-colors text-foreground"
                >
                  {token} <span className="text-muted-foreground">({description})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            {recipientType === 'individual' && (
              <Button
                variant="outline"
                onClick={onSendTestSMS}
                disabled={sending || !message.trim() || !phoneNumber.trim()}
                data-testid="sms-send-test-btn"
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
                Send Test
              </Button>
            )}
            <Button
              onClick={onPreviewRecipients}
              disabled={loading || !message.trim()}
              className="flex-1"
              data-testid="sms-preview-btn"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Preview & Send
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Templates Sidebar */}
      <div className="space-y-4">
        <div className="bg-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">Quick Templates</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenTemplateDialog(null)}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No templates yet. Create one to get started!
            </p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {templates.map(template => (
                <div
                  key={template.id}
                  className="p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors cursor-pointer group"
                  onClick={() => onApplyTemplate(template)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm text-foreground">{template.name}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                        {template.content}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenTemplateDialog(template);
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit2 className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                  {template.usage_count > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Used {template.usage_count} times
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
